import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import { useServices } from '@/context/ServiceContext'
import { Avatar } from '@/components/Avatar'
import { RoleBadge } from '@/components/RoleBadge'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { LevelBadge } from '@/components/gamification/LevelBadge'
import { StreakIndicator } from '@/components/gamification/StreakIndicator'
import type { Organization } from '@/domain/organization'
import type { EpisodeProgress } from '@/domain/progress'
import type { BadgeAward, BadgeDefinition } from '@/domain/badge'
import type { Certificate } from '@/domain/certificate'
import type { StreakRecord } from '@/domain/streak'

export function ProfilePage() {
  const { currentUser, logout } = useAuth()
  const { organizations, progress, xp, badges, certificateRepo, streaks } = useServices()
  const navigate = useNavigate()

  const [org, setOrg] = useState<Organization | null>(null)
  const [myProgress, setMyProgress] = useState<EpisodeProgress[]>([])
  const [xpTotal, setXpTotal] = useState(0)
  const [myBadgeAwards, setMyBadgeAwards] = useState<BadgeAward[]>([])
  const [badgeDefs, setBadgeDefs] = useState<BadgeDefinition[]>([])
  const [certificates, setCertificates] = useState<Certificate[]>([])
  const [streak, setStreak] = useState<StreakRecord | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!currentUser) return
    Promise.all([
      currentUser.orgId ? organizations.getById(currentUser.orgId) : Promise.resolve(null),
      progress.getForUser(currentUser.id),
      xp.getTotalForUser(currentUser.id),
      badges.listAwardsForUser(currentUser.id),
      badges.listDefinitions(),
      certificateRepo.listForUser(currentUser.id),
      streaks.getForUser(currentUser.id),
    ]).then(([orgResult, prog, xpSum, awards, defs, certs, streakRecord]) => {
      setOrg(orgResult)
      setMyProgress(prog)
      setXpTotal(xpSum)
      setMyBadgeAwards(awards)
      setBadgeDefs(defs)
      setCertificates(certs)
      setStreak(streakRecord)
      setLoading(false)
    })
  }, [currentUser, organizations, progress, xp, badges, certificateRepo, streaks])

  if (!currentUser) return null
  if (loading) return <LoadingSpinner label="Loading profile…" />

  const completed = myProgress.filter((p) => p.completed).length
  const started = myProgress.length
  const earnedBadgeDefs = badgeDefs.filter((d) => myBadgeAwards.some((a) => a.badgeKey === d.badgeKey))

  const handleSwitchRole = async () => {
    await logout()
    navigate('/login')
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6 lg:px-8">
      <h1 className="font-display text-3xl font-bold text-white">Profile</h1>

      <div className="mt-6 cyber-panel flex flex-col items-start gap-4 p-6 sm:flex-row sm:items-center">
        <Avatar name={currentUser.displayName} color={currentUser.avatarColor} size={64} />
        <div className="flex-1">
          <p className="font-display text-xl font-semibold text-white">{currentUser.displayName}</p>
          <p className="text-sm text-white/50">{currentUser.email}</p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <RoleBadge role={currentUser.role} />
            {org && <span className="cyber-pill bg-white/5 text-white/60 ring-1 ring-white/10">{org.name}</span>}
          </div>
        </div>
      </div>

      <div className="mt-6 grid gap-4 sm:grid-cols-4">
        <div className="cyber-panel p-5 text-center">
          <p className="text-2xl font-display font-bold text-teal-300">{started}</p>
          <p className="text-xs text-white/50">Episodes started</p>
        </div>
        <div className="cyber-panel p-5 text-center">
          <p className="text-2xl font-display font-bold text-amber-300">{completed}</p>
          <p className="text-xs text-white/50">Episodes watched</p>
        </div>
        <div className="cyber-panel p-5 text-center">
          <p className="text-2xl font-display font-bold gradient-text">{xpTotal}</p>
          <p className="text-xs text-white/50">Cyber Points</p>
        </div>
        <div className="cyber-panel p-5 text-center">
          <p className="text-2xl font-display font-bold text-white">{earnedBadgeDefs.length}</p>
          <p className="text-xs text-white/50">Achievements earned</p>
        </div>
      </div>

      <div className="mt-6 grid gap-4 sm:grid-cols-3">
        <div className="sm:col-span-2">
          <LevelBadge xpTotal={xpTotal} />
        </div>
        <StreakIndicator streak={streak} />
      </div>

      {earnedBadgeDefs.length > 0 && (
        <div className="mt-6 cyber-panel p-6">
          <h2 className="font-display font-semibold text-white">Achievements</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {earnedBadgeDefs.map((b) => (
              <div key={b.badgeKey} className="flex items-center gap-3 rounded-xl bg-white/5 p-3">
                <span className="text-2xl">{b.iconEmoji}</span>
                <div>
                  <p className="text-sm font-semibold text-white">{b.name}</p>
                  <p className="text-xs text-white/50">{b.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {certificates.length > 0 && (
        <div className="mt-6 cyber-panel p-6">
          <h2 className="font-display font-semibold text-white">Certificates</h2>
          <div className="mt-4 space-y-2">
            {certificates.map((c) => (
              <div key={c.id} className="flex items-center justify-between gap-3 rounded-xl bg-white/5 p-3">
                <div>
                  <p className="text-sm font-semibold text-white">{c.courseName}</p>
                  <p className="text-xs text-white/50">
                    {c.status === 'valid' ? 'Issued' : 'Revoked'} {new Date(c.issuedAt).toLocaleDateString()}
                  </p>
                </div>
                <Link to={`/certificates/${c.id}`} className="cyber-btn-secondary !px-4 !py-1.5 text-xs">
                  View
                </Link>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-8 cyber-panel p-6">
        <h2 className="font-display font-semibold text-white">Account details</h2>
        <dl className="mt-4 grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-white/40">Organization ID</dt>
            <dd className="text-white/80">{currentUser.orgId ?? 'None (individual learner)'}</dd>
          </div>
          <div>
            <dt className="text-white/40">Locale</dt>
            <dd className="text-white/80">{currentUser.locale.toUpperCase()}</dd>
          </div>
          <div>
            <dt className="text-white/40">Leaderboard visibility</dt>
            <dd className="text-white/80">{currentUser.leaderboardOptOut ? 'Opted out' : 'Visible'}</dd>
          </div>
          <div>
            <dt className="text-white/40">Auth mode</dt>
            <dd className="text-white/80">Zero-Cost Development (DevAuthProvider)</dd>
          </div>
        </dl>
      </div>

      <div className="mt-8 flex flex-col gap-3 sm:flex-row">
        <button onClick={handleSwitchRole} className="cyber-btn-secondary">
          Switch demo role
        </button>
        <Link to="/dashboard" className="cyber-btn-primary">
          Back to Dashboard
        </Link>
      </div>
    </div>
  )
}
